import {
  useErrorHandlingMutation,
  useErrorHandlingSubscription,
} from '@backoffice-frontend/data-fetching'
import {
  FccDoorActuationError,
  FccRequestStatus,
  FccTargetDoorStatus,
} from '@backoffice-frontend/graphql/subscription'

import type { ReactNode } from 'react'
import { createContext, useState } from 'react'
import {
  type AdVehicleStreamAdVehicleDoorStatusFragment,
  useAdVehicleStreamSendDoorRequestMutation,
} from './AdVehicleDoorStatus.hook'
import { useAdVehicleStreamDoorRequestStatusSubscription } from './AdVehicleDoorStatus.subscription.hook'

type AdVehicleDoorStatusValue = {
  handleDoorRequest: () => void
  requestedDoorStatus: FccTargetDoorStatus | undefined
  currentDoorStatus: FccTargetDoorStatus | undefined
  isRequestPending: boolean
  errors: FccDoorActuationError[]
}

export const AdVehicleDoorStatusContext = createContext<
  AdVehicleDoorStatusValue | undefined
>(undefined)

export const AdVehicleDoorStatusProvider = ({
  vehicle,
  children,
}: {
  vehicle: AdVehicleStreamAdVehicleDoorStatusFragment
  children: ReactNode
}) => {
  const [status, setStatus] = useState<FccRequestStatus | undefined>(undefined)
  const [errors, setErrors] = useState<FccDoorActuationError[]>([])
  const [requestedDoorStatus, setRequestedDoorStatus] = useState<
    FccTargetDoorStatus | undefined
  >(undefined)

  const vehicleId = vehicle.id

  const rightSlidingDoorOpen =
    vehicle.latestADTelemetry?.rightSlidingDoorOpen?.value ?? undefined

  const currentDoorStatus =
    rightSlidingDoorOpen !== undefined
      ? rightSlidingDoorOpen
        ? FccTargetDoorStatus.Open
        : FccTargetDoorStatus.Closed
      : undefined

  useErrorHandlingSubscription(
    useAdVehicleStreamDoorRequestStatusSubscription,
    {
      variables: { vehicleId },
      fetchPolicy: 'no-cache',
      onData: ({ data }) => {
        const doorStatus = data.data?.fccDoorRequestStatus
        if (!doorStatus) {
          return
        }
        const { status, errors } = doorStatus
        setStatus(status)
        setErrors(errors)

        if (
          status === FccRequestStatus.Success ||
          status === FccRequestStatus.Failure
        ) {
          setRequestedDoorStatus(undefined)
        }
        if (status === FccRequestStatus.Success) {
          setStatus(FccRequestStatus.Success)
          setErrors([])
        }
      },
      onError: (e: unknown) => {
        console.error('Error in door status subscription', e)
        setStatus(undefined)
      },
    },
  )

  const [sendDoorRequest, { loading: mutationLoading }] =
    useErrorHandlingMutation(useAdVehicleStreamSendDoorRequestMutation, {
      onError: () => {
        console.error('Error in door request mutation')
        setErrors([FccDoorActuationError.UnknownError])
        setRequestedDoorStatus(undefined)
      },
    })

  const handleDoorRequest = () => {
    if (!currentDoorStatus) {
      console.error('Current door status is not known')
      setErrors([FccDoorActuationError.UnknownError])
      return
    }
    const requestedDoorStatus = nextDoorStatus(currentDoorStatus)
    setStatus(FccRequestStatus.Initiated) // set button to pending until subscription message is received
    setRequestedDoorStatus(requestedDoorStatus)
    setErrors([])
    sendDoorRequest({
      variables: { vehicleId, desiredDoorStatus: requestedDoorStatus },
    })
  }

  return (
    <AdVehicleDoorStatusContext.Provider
      value={{
        handleDoorRequest,
        requestedDoorStatus,
        currentDoorStatus,
        errors,
        isRequestPending:
          mutationLoading || status === FccRequestStatus.Initiated,
      }}
    >
      {children}
    </AdVehicleDoorStatusContext.Provider>
  )
}

const nextDoorStatus = (doorStatus: FccTargetDoorStatus) =>
  doorStatus === FccTargetDoorStatus.Closed
    ? FccTargetDoorStatus.Open
    : FccTargetDoorStatus.Closed
